import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SpotifyLogo, ArrowUpRight, LinkSimple } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString();
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function Analytics() {
  const [data, setData] = useState(null);
  const [spot, setSpot] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then(({ data }) => setData(data)).catch(() => setData({ totals: {}, by_platform: [], by_release: [], by_period: [], report_count: 0 }));
    api.get("/analytics/spotify").then(({ data }) => setSpot(data)).catch(() => setSpot({ configured: false, error: true }));
  }, []);

  if (!data) return <div className="border border-zinc-800 p-12 text-center text-zinc-500 text-xs tracking-widest">LOADING ANALYTICS...</div>;
  const t = data.totals || {};
  const maxStreams = Math.max(1, ...data.by_platform.map(p => p.streams));

  return (
    <section className="mb-14" data-testid="analytics-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight">Streaming Analytics</h2>
          <p className="text-zinc-500 text-xs mt-1">
            {data.report_count ? `From DSP royalty reports · last period ${data.last_period}` : "No DSP reports posted yet. Numbers appear once your first monthly report lands."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 border border-zinc-800 mb-px" data-testid="analytics-totals">
        <Big n={fmt(t.streams)} l="Total Streams" />
        <Big n={fmt(t.saves)} l="Saves / Library Adds" />
        <Big n={usd(t.royalty_usd)} l={data.has_splits ? `Your Royalty Share · of ${usd(t.gross_royalty_usd)} gross` : "Royalties Earned"} accent />
      </div>

      <div className="grid lg:grid-cols-12 border border-zinc-800 border-t-0">
        <div className="lg:col-span-7 p-6 border-r border-zinc-800">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-4">By Platform</div>
          {data.by_platform.length === 0 ? <Empty /> : (
            <div className="space-y-3" data-testid="analytics-by-platform">
              {data.by_platform.map(p => (
                <div key={p.platform} data-testid={`platform-stat-${p.platform.toLowerCase().replace(/[^a-z]/g,'-')}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="uppercase tracking-wider">{p.platform}</span>
                    <span className="text-zinc-400 font-mono">{fmt(p.streams)} · {fmt(p.saves)} saves · <span className="text-white">{usd(p.royalty_usd)}</span></span>
                  </div>
                  <div className="h-1.5 bg-zinc-900"><div className="h-full bg-red-600" style={{ width: `${(p.streams / maxStreams) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
          {data.by_period.length > 1 && (
            <div className="mt-8">
              <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-3">Monthly Streams</div>
              <div className="flex items-end gap-1 h-24" data-testid="analytics-monthly">
                {data.by_period.map(m => {
                  const mx = Math.max(...data.by_period.map(x => x.streams), 1);
                  return <div key={m.period} title={`${m.period}: ${fmt(m.streams)}`} className="flex-1 bg-zinc-800 hover:bg-red-600 transition-colors" style={{ height: `${Math.max(4, (m.streams / mx) * 100)}%` }} />;
                })}
              </div>
              <div className="flex justify-between text-[10px] text-zinc-600 mt-1 font-mono"><span>{data.by_period[0].period}</span><span>{data.by_period.at(-1).period}</span></div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-4">Top Releases</div>
          {data.by_release.length === 0 ? <Empty /> : (
            <div className="divide-y divide-zinc-800" data-testid="analytics-by-release">
              {data.by_release.slice(0, 6).map((r, i) => (
                <div key={r.release_id} className="flex items-center gap-3 py-3 text-sm">
                  <span className="font-display text-zinc-600 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <span className="flex-1 truncate">{r.title || r.release_id}</span>
                  <span className="font-mono text-xs text-zinc-400">{fmt(r.streams)}</span>
                  <span className="font-mono text-xs w-16 text-right">{usd(r.royalty_usd)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SpotifyCard spot={spot} onSaved={(d) => setSpot(d)} />
    </section>
  );
}

function SpotifyCard({ spot, onSaved }) {
  const { user, refresh } = useAuth();
  const [url, setUrl] = useState(user?.spotify_artist_url || "");
  const [apple, setApple] = useState(user?.apple_artist_url || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setUrl(user?.spotify_artist_url || ""); setApple(user?.apple_artist_url || ""); }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me/profile", { spotify_artist_url: url || null, apple_artist_url: apple || null });
      await refresh();
      const { data } = await api.get("/analytics/spotify");
      onSaved(data);
      toast.success("Artist links saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-800 border-t-0 grid lg:grid-cols-12" data-testid="spotify-card">
      <div className="lg:col-span-5 p-6 border-r border-zinc-800">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-4"><SpotifyLogo size={14} weight="fill" className="text-green-500" /> Live Public Stats · Spotify</div>
        <div className="space-y-3">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://open.spotify.com/artist/..." data-testid="spotify-artist-url"
            className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-xs focus:border-red-600 focus:outline-none" />
          <input value={apple} onChange={(e) => setApple(e.target.value)} placeholder="https://music.apple.com/artist/... (for pre-save pages)" data-testid="apple-artist-url"
            className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-xs focus:border-red-600 focus:outline-none" />
          <button onClick={save} disabled={busy} data-testid="save-artist-links" className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 px-4 py-2 hover:border-white transition-colors inline-flex items-center gap-2">
            <LinkSimple size={12} /> {busy ? "Saving..." : "Save Links"}
          </button>
        </div>
        {spot && !spot.configured && <p className="text-[10px] text-yellow-500/80 mt-4 tracking-wider uppercase" data-testid="spotify-not-configured">Spotify API keys pending — live stats activate once connected.</p>}
      </div>
      <div className="lg:col-span-7 p-6">
        {!spot || !spot.linked || !spot.artist ? (
          <div className="text-zinc-600 text-xs h-full flex items-center">Paste your Spotify artist link to pull followers, popularity score, and top-track rankings straight from Spotify.</div>
        ) : (
          <div data-testid="spotify-stats">
            <div className="flex items-center gap-4 mb-5">
              {spot.artist.image && <img src={spot.artist.image} alt="" className="w-14 h-14 object-cover grayscale" />}
              <div>
                <div className="font-display text-xl uppercase tracking-tight">{spot.artist.name}</div>
                <a href={spot.artist.url} target="_blank" rel="noreferrer" className="text-[10px] tracking-widest uppercase text-green-500 inline-flex items-center gap-1">Open on Spotify <ArrowUpRight size={10} /></a>
              </div>
              {spot.extended_access ? (
                <div className="ml-auto grid grid-cols-2 gap-6 text-right">
                  <div><div className="font-display text-3xl tracking-tighter" data-testid="spotify-followers">{fmt(spot.artist.followers)}</div><div className="text-[10px] tracking-widest uppercase text-zinc-500">Followers</div></div>
                  <div><div className="font-display text-3xl tracking-tighter text-red-500">{spot.artist.popularity}</div><div className="text-[10px] tracking-widest uppercase text-zinc-500">Popularity</div></div>
                </div>
              ) : (
                <div className="ml-auto text-right"><div className="font-display text-3xl tracking-tighter" data-testid="spotify-catalog-count">{spot.catalog?.length || 0}</div><div className="text-[10px] tracking-widest uppercase text-zinc-500">Releases on Spotify</div></div>
              )}
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">{spot.top_tracks.length ? "Top Tracks" : "Latest on Spotify"}</div>
            <div className="divide-y divide-zinc-800" data-testid="spotify-list">
              {(spot.top_tracks.length ? spot.top_tracks.slice(0, 5) : (spot.catalog || []).slice(0, 5)).map((t, i) => (
                <a key={t.url} href={t.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 py-2 text-xs hover:text-red-400">
                  <span className="font-display text-zinc-600 w-5">{i + 1}</span><span className="flex-1 truncate">{t.name}</span>
                  <span className="text-zinc-500 truncate max-w-[30%]">{t.album || `${t.type} · ${t.release_date}`}</span>
                  <span className="font-mono w-8 text-right">{t.popularity ?? t.total_tracks}</span>
                </a>
              ))}
            </div>
            {!spot.extended_access && <p className="text-[10px] text-zinc-600 mt-4 leading-relaxed" data-testid="spotify-dev-mode-note">Follower &amp; popularity metrics unlock once your Spotify app is granted extended quota (Spotify Developer Dashboard → Request Extension).</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Big({ n, l, accent }) {
  return (
    <div className={`p-6 border-r border-zinc-800 last:border-r-0 ${accent ? "bg-red-600/5" : ""}`}>
      <div className={`font-display text-3xl lg:text-5xl tracking-tighter ${accent ? "text-red-500" : ""}`}>{n}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">{l}</div>
    </div>
  );
}
function Empty() { return <div className="text-zinc-600 text-xs py-6">— No data yet.</div>; }
